import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Building2, Rocket } from "lucide-react";
import { api, money } from "../lib/api";
import { apiError } from "../lib/auth";
import { PageHeader, NeonButton } from "../components/common";
import { Slider } from "../components/ui/slider";

const Stat = ({ k, v, testid }) => (
  <div className="p-5 border-r border-b border-line relative overflow-hidden" data-testid={testid}>
    <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "linear-gradient(rgb(var(--brand-rgb)) 1px, transparent 1px), linear-gradient(90deg, rgb(var(--brand-rgb)) 1px, transparent 1px)", backgroundSize: "14px 14px" }} />
    <p className="label-xs relative">{k}</p><p className="font-mono text-3xl font-extrabold text-brand mt-2 relative tabular">{v}</p>
  </div>
);

const Merchants = ({ rows }) => (
  <div className="panel overflow-x-auto" data-testid="partner-merchants">
    <table className="w-full text-sm">
      <thead><tr className="text-left label-xs border-b border-line">{["Merchant", "Whop company", "Verified", "Payments", "Disputes", "Win rate", "Recovered", "Autopilot"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
      <tbody>{rows.map((m) => (
        <tr key={m.id} className="border-b border-divider" data-testid={`partner-row-${m.id}`}>
          <td className="px-4 py-3 font-bold">{m.name}<span className="block font-mono text-[10px] text-white/40">{m.source}</span></td>
          <td className="px-4 py-3 font-mono text-xs text-white/60">{m.whop_company_id || "direct webhook"}</td>
          <td className="px-4 py-3 font-mono">{m.verifications}</td><td className="px-4 py-3 font-mono">{m.payments}</td><td className="px-4 py-3 font-mono">{m.disputes}</td>
          <td className="px-4 py-3 font-mono text-brand">{m.win_rate == null ? "—" : `${m.win_rate}%`}</td><td className="px-4 py-3 font-mono">{money(m.recovered)}</td><td className="px-4 py-3 font-mono">{m.autopilot_actions}</td>
        </tr>))}</tbody>
    </table>
  </div>
);

const RevenueModel = () => {
  const [v, setV] = useState({ merchants: 500, share: 30, avgDisputes: 8, avgAmount: 120, winRate: 55 });
  const set = (k) => (x) => setV({ ...v, [k]: x[0] });
  const r = useMemo(() => {
    const recoveredPerMerchant = v.avgDisputes * v.avgAmount * (v.winRate / 100) * 12;
    const success = v.merchants * recoveredPerMerchant * 0.15;
    const sub = v.merchants * 99 * 12;
    return { success, sub, whopSuccess: success * v.share / 100, whopSub: sub * v.share / 100, merchantNet: recoveredPerMerchant * 0.85 };
  }, [v]);
  const rows = [["Merchants on AEGIS", "merchants", 10, 5000, 10], ["Whop's share (%)", "share", 30, 50, 1], ["Disputes / merchant / month", "avgDisputes", 1, 60, 1],
    ["Avg dispute ($)", "avgAmount", 20, 1000, 10], ["Win rate (%)", "winRate", 20, 85, 1]];
  const chart = [{ name: "15% success fee", Whop: Math.round(r.whopSuccess), AEGIS: Math.round(r.success - r.whopSuccess) }, { name: "$99 / month", Whop: Math.round(r.whopSub), AEGIS: Math.round(r.sub - r.whopSub) }];
  return (
    <section className="panel p-6" data-testid="partner-revenue-model">
      <p className="label-xs">Partnership revenue model · annual · estimates only</p>
      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8 mt-4">
        <div className="space-y-5">
          {rows.map(([l, k, min, max, step]) => (
            <div key={k}><div className="flex justify-between text-sm"><span>{l}</span><span className="font-mono text-brand" data-testid={`rm-value-${k}`}>{v[k]}</span></div>
              <Slider className="mt-2" min={min} max={max} step={step} value={[v[k]]} onValueChange={set(k)} data-testid={`rm-slider-${k}`} /></div>
          ))}
        </div>
        <div>
          <div className="grid grid-cols-2 gap-3">
            <div className="border border-line p-4" data-testid="rm-option-a"><p className="label-xs">Option A · 15% of recovered</p><p className="font-mono text-2xl font-extrabold text-brand mt-1">{money(r.whopSuccess)}</p><p className="text-xs text-white/50">to Whop / yr · merchants pay $0 if they lose</p></div>
            <div className="border border-line p-4" data-testid="rm-option-b"><p className="label-xs">Option B · $99 / month</p><p className="font-mono text-2xl font-extrabold text-brand mt-1">{money(r.whopSub)}</p><p className="text-xs text-white/50">to Whop / yr · predictable SaaS revenue</p></div>
          </div>
          <div className="h-56 mt-5"><ResponsiveContainer><BarChart data={chart}><XAxis dataKey="name" stroke="#666" fontSize={11} /><YAxis stroke="#666" fontSize={10} tickFormatter={(x) => `$${Math.round(x / 1000)}k`} /><Tooltip formatter={(x) => money(x)} contentStyle={{ background: "#0b0d10", border: "1px solid #333" }} />
            <Bar dataKey="Whop" stackId="a" fill="rgb(var(--brand-rgb))" /><Bar dataKey="AEGIS" stackId="a" fill="rgb(var(--brand-rgb)/0.35)" /></BarChart></ResponsiveContainer></div>
          <p className="text-xs text-white/50 mt-3">Each merchant keeps about {money(r.merchantNet)} / yr in recovered revenue (after the 15% fee). Option C (white-label or acquisition) is usually valued at a multiple of ARR; negotiate it after the pilot proves the numbers.</p>
          <p className="text-[11px] text-amber-300/80 mt-2" data-testid="rm-disclaimer">Estimates only, from the inputs above. These are not actual results or guarantees.</p>
        </div>
      </div>
    </section>
  );
};

const DevInstall = ({ reload }) => {
  const [f, setF] = useState({ company_id: "biz_", company_name: "", email: "", name: "" });
  const go = async () => {
    try {
      const { data } = await api.post("/admin/whop-app/dev-install", f);
      toast.success(data.installed_now ? `Installed for ${data.workspace.name}` : "Already installed. Opening it");
      window.open(`${data.dashboard_path}#dev_token=${encodeURIComponent(data.token)}`, "_blank");
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };
  const inp = "bg-field border border-fieldline px-3 py-2 text-sm focus:outline-none focus:border-brand";
  return (
    <section className="panel p-5" data-testid="partner-dev-install">
      <p className="label-xs flex items-center gap-2"><Rocket className="w-3.5 h-3.5 text-brand" /> Simulate a one-click Whop App install (preview)</p>
      <p className="text-xs text-white/50 mt-1">Creates an isolated merchant workspace exactly as a real install does, then opens the in-Whop dashboard view. Real installs activate once WHOP_APP_ID and the App API key are added.</p>
      <div className="grid sm:grid-cols-4 gap-2 mt-3">
        {[["company_id", "biz_…"], ["company_name", "Store name"], ["email", "Admin email"], ["name", "Admin name"]].map(([k, p]) => <input key={k} placeholder={p} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inp} data-testid={`dev-install-${k}`} />)}
      </div>
      <NeonButton className="mt-3" onClick={go} data-testid="dev-install-btn"><Building2 className="w-3 h-3" /> Install AEGIS on this company</NeonButton>
    </section>
  );
};

export default function Partner() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/admin/partner/overview").then((r) => setD(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <p className="font-mono text-xs text-white/40">loading partner view…</p>;
  const t = d.totals;
  return (
    <div className="space-y-6" data-testid="partner-page">
      <PageHeader eyebrow="// whop partner · platform-wide chargeback defense" title="Whop Partner Dashboard" />
      <div className="panel grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 border-l border-t border-line">
        <Stat k="Merchants protected" v={t.merchants} testid="pt-merchants" /><Stat k="Verifications" v={t.verifications} testid="pt-verifications" />
        <Stat k="Disputes" v={t.disputes} testid="pt-disputes" /><Stat k="Recovered" v={money(t.recovered)} testid="pt-recovered" />
        <Stat k="Platform win rate" v={t.platform_win_rate == null ? "—" : `${t.platform_win_rate}%`} testid="pt-winrate" /><Stat k="Median merchant win rate" v={t.median_win_rate == null ? "—" : `${t.median_win_rate}%`} testid="pt-median" />
      </div>
      <Merchants rows={d.merchants} />
      <RevenueModel />
      <DevInstall reload={load} />
    </div>
  );
}
